import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
const AuthContext = createContext(null);
export function AuthProvider({ children }) {
    const queryClient = useQueryClient();
    const [isLoggedIntoStorage, setIsLoggedIntoStorage] = React.useState(() => localStorage.getItem('isLoggedIn') === 'true');
    const { data: user, isLoading, isFetching } = useQuery({
        queryKey: ['authUser'],
        queryFn: async () => {
            try {
                const response = await api.get('/api/v1/profile');
                return response.data;
            }
            catch (err) {
                localStorage.removeItem('isLoggedIn');
                setIsLoggedIntoStorage(false);
                return null;
            }
        },
        // If we have a user and they are unverified, poll every 5s so the badge updates.
        refetchInterval: (query) => {
            const currentUser = query.state.data;
            if (currentUser && currentUser.isVerified === false) {
                return 5000;
            }
            return false;
        },
        retry: false,
        staleTime: 5 * 60 * 1000,
        enabled: isLoggedIntoStorage,
    });
    const logoutMutation = useMutation({
        mutationFn: () => api.post('/auth/logout'),
        onSuccess: () => {
            localStorage.removeItem('isLoggedIn');
            setIsLoggedIntoStorage(false);
            queryClient.setQueryData(['authUser'], null);
        },
    });
    const login = useCallback(() => {
        localStorage.setItem('isLoggedIn', 'true');
        setIsLoggedIntoStorage(true);
        queryClient.invalidateQueries({ queryKey: ['authUser'] });
    }, [queryClient]);
    const logout = useCallback(() => {
        logoutMutation.mutate();
    }, [logoutMutation]);
    const loginWithGoogleMutation = useMutation({
        mutationFn: (credential) => api.post('/auth/google', { credential }),
        onSuccess: (response) => {
            localStorage.setItem('isLoggedIn', 'true');
            setIsLoggedIntoStorage(true);
            queryClient.setQueryData(['authUser'], response.data.user);
        },
    });
    const loginWithGoogle = useCallback(async (credential) => {
        await loginWithGoogleMutation.mutateAsync(credential);
    }, [loginWithGoogleMutation]);
    const value = {
        user: user ?? null,
        loading: isLoading || isFetching,
        isAuthenticated: !!user,
        login,
        loginWithGoogle,
        logout,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx)
        throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
